import { useCallback, useEffect, useState } from 'react';

/**
 * Detect whether the browser exposes the Fullscreen API at all. We
 * check both the standard names and Safari's vendor-prefixed ones, so
 * the toggle button can hide itself gracefully on environments that
 * don't support fullscreen (older Safari, some embedded webviews).
 *
 * Returns a static boolean — once mounted, the API doesn't appear or
 * disappear at runtime, so we read it once.
 */
export function isFullscreenSupported(): boolean {
  if (typeof document === 'undefined') return false;
  const d = document as Document & {
    webkitFullscreenEnabled?: boolean;
    webkitFullscreenElement?: Element | null;
  };
  return Boolean(
    d.fullscreenEnabled ||
      // biome-ignore lint/suspicious/noExplicitAny: vendor prefix check
      (d as any).webkitFullscreenEnabled,
  );
}

interface VendorDoc extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
}
interface VendorElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
}

function currentFullscreenElement(): Element | null {
  if (typeof document === 'undefined') return null;
  const d = document as VendorDoc;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

/**
 * Hook around the browser Fullscreen API. Exposes:
 *
 *   - isFullscreen: live boolean, kept in sync with `fullscreenchange`
 *     so it flips back to `false` when the user presses Esc.
 *   - toggle(): enter when not in fullscreen, exit when in. Resolves
 *     after the request completes; swallows the (rare) browser-rejected
 *     promise (browsers reject when the call isn't user-initiated, but
 *     the button click always is).
 *   - supported: false on environments without the API; callers should
 *     hide the button rather than render a broken control.
 */
export function useFullscreen(): {
  isFullscreen: boolean;
  toggle: () => Promise<void>;
  supported: boolean;
} {
  const [isFullscreen, setIsFullscreen] = useState<boolean>(
    () => currentFullscreenElement() !== null,
  );
  const supported = isFullscreenSupported();

  useEffect(() => {
    if (!supported) return;
    const onChange = () => setIsFullscreen(currentFullscreenElement() !== null);
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, [supported]);

  const toggle = useCallback(async () => {
    if (!supported) return;
    const d = document as VendorDoc;
    if (currentFullscreenElement() === null) {
      const el = document.documentElement as VendorElement;
      const req = el.requestFullscreen ?? el.webkitRequestFullscreen;
      if (req) {
        try {
          await req.call(el);
        } catch {
          // Browsers can reject when the call isn't gesture-attributed;
          // the click handler is, so this is rare. Silent fail keeps
          // the UI stable.
        }
      }
    } else {
      const exit = document.exitFullscreen ?? d.webkitExitFullscreen;
      if (exit) {
        try {
          await exit.call(document);
        } catch {
          // ignore
        }
      }
    }
  }, [supported]);

  return { isFullscreen, toggle, supported };
}
